/**
 * The four GAEB extensions, priced and unpriced, in both generations: `x83`/`x84` are GAEB DA XML
 * and `d83`/`d84` are GAEB 90. The `84` of each pair carries prices and the `83` does not, which is
 * why an import of a `84` wants its `83` beside it.
 */
const GAEB_EXTENSIONS = /\.(x84|x83|d84|d83)$/i;

/**
 * Whether a file name names a GAEB document.
 *
 * A GAEB file *is* a Leistungsverzeichnis — that is the whole of the format — so the extension is
 * a stronger statement about what the document is than anything a model could read out of it, and
 * it is the only statement there is: GAEB carries no prose for an extractor to window over.
 *
 * @param fileName - The name the file was uploaded as.
 * @returns Whether it is one of the four GAEB extensions.
 */
export function isGaebFileName(fileName: string | null | undefined): boolean {
	return !!fileName && GAEB_EXTENSIONS.test(fileName);
}

/**
 * Whether a file is one the offer import can read at all.
 *
 * The importer has two extractors and no others: the GAEB parser, and the PDF service extraction.
 * Anything else — a photograph of an Angebot, a Word document, a spreadsheet — has no path into a
 * calculation, so offering the action for it would be offering a failure.
 *
 * @param fileName - The name the file was uploaded as.
 * @returns Whether the offer import has an extractor for it.
 */
export function isOfferImportFileName(fileName: string | null | undefined): boolean {
	return isGaebFileName(fileName) || (!!fileName && /\.pdf$/i.test(fileName));
}

/**
 * The unpriced counterpart a priced GAEB file wants beside it, by extension.
 *
 * An `x84`/`d84` holds the prices and its `x83`/`d83` holds the positions, and the extractor reads
 * the two together. Anything else returns null, including the unpriced file itself: an `x83` alone
 * imports fine, it simply imports without prices.
 *
 * @param fileName - The name the file was uploaded as.
 * @returns The extension of the file that completes the pair, or null when nothing does.
 */
export function gaebCounterpartExtension(fileName: string | null | undefined): string | null {
	if (!fileName) return null;
	if (/\.x84$/i.test(fileName)) return "x83";
	if (/\.d84$/i.test(fileName)) return "d83";
	return null;
}
